import { useState } from "react";
import NavBar from "../navbar/navbar";
import { Box } from "@mui/material";
import SelectedFixture from "../roundview/components/selectedfixture/selectedfixture";
import useInterval from "react-useinterval";
import useDetailRound from "../rounds/useDetailRound";
import Progress from "../common/progress";
import { isFixtureAfterGameStart } from "../common/DateTimeHelper";
import useDetailedFixture from "../fixtures/useDetailedFixture";
import { IsRightToLeftTheme } from "../theme/themehelper";
import SlimCoupon from "../slimcoupon/slimcoupon";
import CouponWinHeader from "./couponwinheader";
import BestRows from "./bestrows";
import RowsStatistics from "./rowsstatistics";
import CouponInfo from "./couponinfo";
import { Fixture } from "../models/fixture";
import { DetailedRound, Round } from "../models/round";
import { PoolXMoney } from "../models/money";
import { useMediaQuery } from "@mui/material";
import { Bet, CorrectRow, Coupon, Leg, System } from "../models/couponshares";
import usePublicCouponShare from "../couponShares/usePublicCouponShare";
import WinInfo from "./wininfo";
import SurvivorCoupon from "./survivorcoupon";


/* ------------------------- Types (Adjust if needed) ------------------------- */

interface ContentAreaProps {
    fixture?: Fixture;
    theme: string;
    round: Round;
}

/* --------------------------- Component: ContentArea -------------------------- */

const ContentArea: React.FC<ContentAreaProps> = ({ fixture, theme, round }) => {
    
    const { fixtureDetails } = useDetailedFixture(fixture?.id ?? 0);

    if (fixture) {
        const isAfterGameStart = isFixtureAfterGameStart(fixture);
        return (
            <SelectedFixture
                fixtureDetail={fixtureDetails}
                isAfterGameStart={isAfterGameStart}
                roundFixture={fixture}
                theme={theme}
                round={round}
            />
        );
    }

    return <Progress />;
};

/* ------------------------ Helper: calcCorrectSigns ------------------------- */

function calcCorrectSigns(row: Leg, correctRow: CorrectRow): number {
    let numCorrect = 0;

    for (let i = 0; i < correctRow.signs.length; i++) {
        if (row.signs[i] === correctRow.signs[i]) {
            numCorrect++;
        }
    }

    return numCorrect;
}

/* ---------------------------- Main Component ---------------------------- */

interface PublicCouponProps {
    publicCouponId: string;
}

const PublicCoupon: React.FC<PublicCouponProps> = (props) => {
    const isRightToLeft = IsRightToLeftTheme((window as any).theme);

    const isSmallScreen = useMediaQuery("(max-width: 950px)");

    const initialFixtureIndexSelected = isSmallScreen ? -1 : 0;
    const [fixtureValue, setFixtureValue] = useState<number>(initialFixtureIndexSelected);
    const couponShare = usePublicCouponShare(props.publicCouponId)[0];

    const roundId = couponShare?.coupon?.roundId;
    const detailRoundResponse = useDetailRound(roundId);
    const round = detailRoundResponse?.round as DetailedRound | undefined;

    useInterval(() => {
        detailRoundResponse?.reloadDetailRound?.();
    }, 20000);

    if (!couponShare?.coupon) return null;
    if (detailRoundResponse.status === "loading") return <div></div>;

    if (round && round.name.toLowerCase().indexOf("survivor") !== -1) {
        return (
            <>
                <NavBar whichToUnderline="3" balanceUpdateId={0} />
                <SurvivorCoupon publicCouponId={props.publicCouponId} />
            </>
        );
    }

    const fixture =
        fixtureValue === undefined || !round ? undefined : round.fixtures[fixtureValue];

    const isAfterDeadline = round ? new Date(round.openTo) < new Date() : false;

    const deepCoupon = couponShare.coupon.coupon;
    const detailedBets: Bet[] | undefined = deepCoupon?.bets;
    const correctRow = couponShare.coupon.correctRow;

    const preliminaryOutcome: Record<number, number> = {};
    let maxCorrectSigns = 0;

    if (detailedBets && correctRow) {
        for (const betKey in detailedBets) {
            const betRows = detailedBets[betKey].rows;

            for (const rowKey in betRows) {
                const row = betRows[rowKey as any];
                const correctSigns = calcCorrectSigns(row, correctRow);

                maxCorrectSigns = Math.max(maxCorrectSigns, correctSigns);
                preliminaryOutcome[correctSigns] = (preliminaryOutcome[correctSigns] ?? 0) + 1;
            }
        }
    }

    let betResult: PoolXMoney = { amount: 0, currency: round?.operatorTurnover?.currency ?? "SEK" };

    if (couponShare.operatorPreliminaryResult) {
        betResult = couponShare.operatorPreliminaryResult;
    }

    if (couponShare.operatorResult) {
        betResult = couponShare.operatorResult;
    }

    const theme: string = (window as any).theme;

    let chosenRSystem: System = { type: "MATHEMATICAL" };

    if (deepCoupon?.bets) {
        const firstBetKey = Object.keys(deepCoupon.bets)[0] as any;
        if (firstBetKey) {
            chosenRSystem = deepCoupon.bets[firstBetKey].system ?? chosenRSystem;
        }
    }

    if (round === undefined) {
        return <></>;
    }

    /* ------------------------- Small Screen Layout ------------------------- */

    if (isSmallScreen) {
        return (
            <>
                <NavBar whichToUnderline="3" balanceUpdateId={0} />
                <Box sx={{
                    display: 'flex',
                    ml: 'var(--coupon-left-margin)',
                    flexDirection: 'var(--flex-direction)',
                    '@media screen and (max-width: 950px)': {
                        flexDirection: 'column',
                        mt: 0,
                    },
                }}>
                    <Box sx={{
                        '@media screen and (min-width: 951px)': { minWidth: '402px' },
                    }}>
                        <CouponWinHeader
                            winAmount={betResult}
                            nCorrectRows={maxCorrectSigns}
                            isRightToLeft={isRightToLeft}
                            isHidden={couponShare.couponHidden}
                        />

                        <Box sx={{ borderBottom: '1px solid #060707', height: '1px' }} />

                        <SlimCoupon
                            round={round}
                            theme={theme}
                            coupon={deepCoupon}
                            onChangeFixture={setFixtureValue}
                            couponFixtureSummaries={couponShare.coupon.fixtures}
                            chosenRSystem={chosenRSystem} onSubmit={function (coupon: Coupon): Promise<boolean> {
                                throw new Error("Function not implemented.");
                            }} showProPick={false} setShowProPick={function (show: boolean): void {
                                throw new Error("Function not implemented.");
                            }} showConfirm={false} setShowConfirm={function (show: boolean): void {
                                throw new Error("Function not implemented.");
                            }} submitFailed={false} onErrorMsgClose={function (): void {
                                throw new Error("Function not implemented.");
                            }} submitError={undefined} submitting={false} stakeMultiplier={0} setStakeMultiplier={function (multiplier: number): void {
                                throw new Error("Function not implemented.");
                            }} showRoundInfo={false} setShowRoundInfo={function (show: boolean): void {
                                throw new Error("Function not implemented.");
                            }} showRSystem={false} setShowRSystem={function (show: boolean): void {
                                throw new Error("Function not implemented.");
                            }} proPickRows={undefined} setProPickRows={function (rows: string[][] | undefined): void {
                                throw new Error("Function not implemented.");
                            }} freebet={undefined} onClear={function (): void {
                                throw new Error("Function not implemented.");
                            }} setChosenRSystem={function (system: System): void {
                                throw new Error("Function not implemented.");
                            }} />

                        {isAfterDeadline && (
                            <WinInfo round={round} couponShare={couponShare} isRightToLeft={isRightToLeft} />
                        )}

                        <CouponInfo round={round} couponShare={couponShare} isRightToLeft={isRightToLeft} shareEnabled={false}/>
                        <BestRows round={round} couponShare={couponShare} />
                        <RowsStatistics round={round} couponShare={couponShare} />
                    </Box>
                </Box>
            </>
        );
    }

    /* ----------------------------- Desktop Layout ---------------------------- */

    return (
        <>
            <NavBar whichToUnderline="3" balanceUpdateId={0} />
            <Box sx={{
                display: 'flex',
                ml: 'var(--coupon-left-margin)',
                flexDirection: 'var(--flex-direction)',
                '@media screen and (max-width: 950px)': {
                    flexDirection: 'column',
                    mt: 0,
                },
            }}>
                <Box sx={{
                    '@media screen and (min-width: 951px)': { minWidth: '402px' },
                }}>
                    <CouponWinHeader
                        winAmount={betResult}
                        nCorrectRows={maxCorrectSigns}
                        isRightToLeft={isRightToLeft}
                        isHidden={couponShare.couponHidden}
                    />

                    <Box sx={{ borderBottom: '1px solid #060707', height: '1px' }} />

                    <SlimCoupon
                        round={round}
                        theme={theme}
                        coupon={deepCoupon}
                        onChangeFixture={setFixtureValue}
                        couponFixtureSummaries={couponShare.coupon.fixtures}
                        chosenRSystem={chosenRSystem} onSubmit={function (coupon: Coupon): Promise<boolean> {
                            throw new Error("Function not implemented.");
                        }} showProPick={false} setShowProPick={function (show: boolean): void {
                            throw new Error("Function not implemented.");
                        }} showConfirm={false} setShowConfirm={function (show: boolean): void {
                            throw new Error("Function not implemented.");
                        }} submitFailed={false} onErrorMsgClose={function (): void {
                            throw new Error("Function not implemented.");
                        }} submitError={undefined} submitting={false} stakeMultiplier={0} setStakeMultiplier={function (multiplier: number): void {
                            throw new Error("Function not implemented.");
                        }} showRoundInfo={false} setShowRoundInfo={function (show: boolean): void {
                            throw new Error("Function not implemented.");
                        }} showRSystem={false} setShowRSystem={function (show: boolean): void {
                            throw new Error("Function not implemented.");
                        }} proPickRows={undefined} setProPickRows={function (rows: string[][] | undefined): void {
                            throw new Error("Function not implemented.");
                        }} freebet={undefined} onClear={function (): void {
                            throw new Error("Function not implemented.");
                        }} setChosenRSystem={function (system: System): void {
                            throw new Error("Function not implemented.");
                        }} />

                    {isAfterDeadline && (
                        <WinInfo round={round} couponShare={couponShare} isRightToLeft={isRightToLeft} />
                    )}

                    <CouponInfo round={round} couponShare={couponShare} isRightToLeft={isRightToLeft}  shareEnabled={false}/>
                    <BestRows round={round} couponShare={couponShare} />
                    <RowsStatistics round={round} couponShare={couponShare} />
                </Box>

                <Box sx={{
                    '@media screen and (min-width: 951px)': { width: '100%' },
                }}>
                    {fixture &&
                        <ContentArea fixture={fixture} theme={theme} round={round!} />
                    }
                </Box>
            </Box>
        </>
    );
};

export default PublicCoupon;
